<template>
  <div>
    <section-title content="Fecha y Lugar" has-line />

    <div class="row">
      <div class="col-12 col-md-4 mb-3">
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          rules="required"
        >
          <custom-date-picker
            v-model="model.date"
            class="w-100"
            custom-error-msg="Campo requerido."
            :disabled="isReadOnly"
            :error="errors[0]"
            label="Fecha:"
            placeholder="DD/MM/YYYY"
          />
        </validation-provider>
        <div v-else class="d-flex flex-column">
          <label class="text-muted" style="font-size: 14px">Fecha:</label>
          <span>{{ model.date | date }}</span>
        </div>
      </div>

      <div class="col-12 col-md-4 mb-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-input
            v-model="model.addressLine1"
            custom-error-msg="El campo es requerido."
            :disabled="isReadOnly"
            :error="errors[0]"
            label="Dirección (línea 1):"
          />
        </validation-provider>
      </div>

      <div class="col-12 col-md-4 mb-3">
        <custom-input
          v-model="model.addressLine2"
          :disabled="isReadOnly"
          label="Dirección (línea 2):"
        />
      </div>

      <div class="col-12 col-md-4 mb-3">
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          rules="required"
        >
          <custom-drop-down
            v-model="model.country"
            clearable
            custom-error-msg="El campo es requerido."
            :disabled="isReadOnly"
            :error="errors[0]"
            fieldkey="id"
            fieldname="name"
            label="País:"
            :options="countries"
            @input="onCountryChange"
          />
        </validation-provider>
        <div v-else class="d-flex flex-column">
          <label class="text-muted" style="font-size: 14px">País:</label>
          <span>{{ model.country?.name | empty }}</span>
        </div>
      </div>

      <div v-if="isUSA" class="col-12 col-md-4 mb-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-drop-down
            v-model="model.state"
            clearable
            custom-error-msg="El campo es requerido."
            :disabled="isReadOnly"
            :error="errors[0]"
            fieldkey="id"
            fieldname="name"
            label="Estado:"
            :options="states"
            @input="onStateChange"
          />
        </validation-provider>
      </div>

      <div v-if="isPuertoRico" class="col-12 col-md-4 mb-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-drop-down
            v-model="model.municipality"
            clearable
            custom-error-msg="El campo es requerido."
            :disabled="isReadOnly"
            :error="errors[0]"
            fieldkey="id"
            fieldname="name"
            label="Municipio:"
            :options="municipalities"
          />
        </validation-provider>
      </div>

      <div class="col-12 col-md-4 mb-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-input
            v-model="model.zipCode"
            custom-error-msg="El campo es requerido."
            :disabled="isReadOnly"
            :error="errors[0]"
            label="Código postal:"
            mask-type="ZipcodeExt"
          />
        </validation-provider>
      </div>

      <div class="col-12 mb-3">
        <validation-provider v-if="!isReadOnly" v-slot="{ errors }" rules="">
          <custom-text-area
            v-model="model.additionalComments"
            :count-classes="['d-block', 'small-counter', 'text-right']"
            :custom-error-msg="'El comentario es requerido.'"
            :disabled="isReadOnly"
            :error="errors[0]"
            label="Comentario Adicional"
            :maxlength="5000"
            :show-char-count="!isReadOnly"
          />
        </validation-provider>
        <div v-else class="d-flex flex-column">
          <label style="font-size: 14px">Comentario Adicional:</label>
          <span>{{ model.additionalComments | empty }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getCountries,
  getStates,
  getMunicipalities,
} from '@/modules/common/location.api.js';

import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';

import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import CustomTextArea from 'CustomTextArea';
import SectionTitle from '@/shared/components/SectionTitle.vue';
import CustomDatePicker from '@/components/common/form/CustomDatePicker';

const COUNTRY_USA = 63;
const STATE_PUERTO_RICO = 40;

export default {
  name: 'DateAndLocation',
  components: {
    CustomInput,
    CustomDropDown,
    CustomTextArea,
    SectionTitle,
    CustomDatePicker,
  },
  mixins: [storeMixin, alertMixin],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      countries: [],
      states: [],
      municipalities: [],
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    isUSA() {
      return this.model.country?.id === COUNTRY_USA;
    },
    isPuertoRico() {
      return this.model.state?.id === STATE_PUERTO_RICO;
    },
  },
  async mounted() {
    await this.loadCatalogs();
  },
  methods: {
    async loadCatalogs() {
      try {
        const [countries, states, municipalities] = await Promise.all([
          getCountries(),
          getStates(),
          getMunicipalities({ academicYearId: 0 }),
        ]);
        this.countries = (countries?.data || countries) ?? [];
        this.states = (states?.data || states) ?? [];
        this.municipalities = (municipalities?.data || municipalities) ?? [];

        const usaIndex = this.countries.findIndex((c) => c.id === COUNTRY_USA);
        if (usaIndex > -1) {
          const usa = this.countries.splice(usaIndex, 1)[0];
          this.countries.unshift(usa);
        }

        const prIndex = this.states.findIndex(
          (s) => s.id === STATE_PUERTO_RICO
        );
        if (prIndex > -1) {
          const pr = this.states.splice(prIndex, 1)[0];
          this.states.unshift(pr);
        }
      } catch (e) {
        this.ShowToast(
          'Catálogos',
          'No fue posible cargar los catálogos.',
          'error'
        );
      }
    },

    onCountryChange() {
      this.model.state = null;
      this.model.municipality = null;
    },

    onStateChange() {
      this.model.municipality = null;
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.custom-datetime-picker-container.width-container) {
  width: 100% !important  ;
}
</style>
